
/// <amd-module name="Test/Client/TestManagementServices" />
import * as Extended from "Test/Client/RestClient/Extended";
import * as ExtendedTcm from "Test/Client/RestClient/ExtendedTcm";
import * as Test from "Test/Client/RestClient/Test";
import * as Tcm from "Test/Client/RestClient/TestResults";
import { ITestLogWithContinuationToken, TestCaseResultsWithContinuationToken, TestResultsFieldDetailsWithContinuationToken } from "Test/common-util/Contracts/ExtendedTest";
import * as TCMContracts from "Test/common-util/Contracts/Test";
import { getTFSPageData } from "Tfs/Platform/Page";
import { IVssPageContext, IVssReadConsistencyLevel, IVssService, Services, VssService } from "VSS/Platform/Context";
import * as VS_Services from "VSS/Platform/Contracts/WebApi";
import { isFeatureFlagEnabled } from "VSS/Platform/Feature";

export const TestManagementServiceName = "ITestManagementService";

export interface ITestManagementService extends IVssService {
    queryTestResultsReportForBuild(buildId: number, publishContext?: string, includeFailureDetails?: boolean): Promise<TCMContracts.TestResultSummary | null>;
    getTestResultsByGroupForBuild(buildId: number, sourceWorkflow: string, groupBy: string, filter: string, sortBy: string, shouldIncludeResults: boolean, queryRunSummaryForInProgress: boolean): Promise<TCMContracts.TestResultsDetails | null>;
    getTestResultsByGroupForRelease(releaseId: number, releaseEnvId: number, sourceWorkflow: string, groupBy: string, filter: string, sortBy: string, shouldIncludeResults: boolean, queryRunSummaryForInProgress: boolean): Promise<TCMContracts.TestResultsDetails>
    getTestResultsByQuery(query: TCMContracts.TestResultsQuery): Promise<TCMContracts.TestResultsQuery>;
    getResultById(runId: number, resultsId: number, includeDetails?: TCMContracts.ResultDetails): Promise<TCMContracts.TestCaseResult>;
    getTestRunById(runId: number, includeDetails: boolean, includeTags?: boolean): Promise<TCMContracts.TestRun>;
    getCodeCoverageSummary(buildId: number): Promise<TCMContracts.CodeCoverageSummary | null>;
    getTestResultsByBuildWithContinuationToken(buildId: number, publishContext?: string, outcomes?: string, top?: number, continuationToken?: string): Promise<TestCaseResultsWithContinuationToken>;
    getTestResultsByPipeline(pipelineId: number, stageName?: string, phaseName?: string, jobName?: string, outcomes?: TCMContracts.TestOutcome[], top?: number, continuationToken?: String): Promise<VS_Services.PagedList<TCMContracts.ShallowTestCaseResult>>;
    getTestResultsByReleaseWithContinuationToken(releaseId: number, releaseEnvId?: number, publishContext?: string, outcomes?: string, top?: number, continuationToken?: string): Promise<TestCaseResultsWithContinuationToken>;
    getResultGroupsByBuild(buildId: number, publishContext?: string, fields?: string[], continuationToekn?: string): Promise<TestResultsFieldDetailsWithContinuationToken>;
    getResultGroupsByRelease(releaseId: number, releaseEnvId?: number, publishContext?: string, fields?: string[], continuationToken?: string): Promise<TestResultsFieldDetailsWithContinuationToken>;
    getBuildCodeCoverage(buildId: number, flag: number): Promise<TCMContracts.BuildCoverage[] | null>;
    queryTestHistory(filter: TCMContracts.TestHistoryQuery): Promise<TCMContracts.TestHistoryQuery>;
    getRecentBugs(automatedTestName?: string, testCaseId?: number, recentDays?: number): Promise<TCMContracts.WorkItemReference[]>;
    getAssociatedBugs(runId: number, resultId: number): Promise<TCMContracts.WorkItemReference[]>;
    deleteTestMethodToWorkItemLink(testName: string, workItemId: number): Promise<boolean>;
    getTestMethodLinkedWorkItems(testName: string): Promise<TCMContracts.TestToWorkItemLinks>;
    getTestRunAttachments(runId: number): Promise<TCMContracts.TestAttachment[]>;
    getTestResultAttachments(runId: number, testCaseResultId: number): Promise<TCMContracts.TestAttachment[]>;
    getTestSubResultAttachments(runId: number, testCaseResultId: number, testSubResultId: number): Promise<TCMContracts.TestAttachment[]>;
    getTestRunAttachmentContent(runId: number, attachmentId: number): Promise<ArrayBuffer>;
    getTestResultAttachmentContent(runId: number, testCaseResultId: number, attachmentId: number): Promise<ArrayBuffer>;
    getTestSubResultAttachmentContent(runId: number, testCaseResultId: number, attachmentId: number, testSubResultId: number): Promise<ArrayBuffer>;
    getTestRunLogs(runId: number, type: TCMContracts.TestLogType, directoryPath?: string, fileNamePrefix?: string, fetchMetaData?: boolean, top?: number, continuationToken?: string): Promise<ITestLogWithContinuationToken>;
    getTestResultLogs(runId: number, resultId: number, type: TCMContracts.TestLogType, directoryPath?: string, fileNamePrefix?: string, fetchMetaData?: boolean, top?: number, continuationToken?: string): Promise<ITestLogWithContinuationToken>;
    getTestSubResultLogs(runId: number, resultId: number, subResultId: number, type: TCMContracts.TestLogType, directoryPath?: string, fileNamePrefix?: string, fetchMetaData?: boolean, top?: number, continuationToken?: string): Promise<ITestLogWithContinuationToken>;
    getTestLogStoreEndpointDetailsForRunLog(runId: number, type: TCMContracts.TestLogType, filePath: string): Promise<TCMContracts.TestLogStoreEndpointDetails>;
    getTestLogStoreEndpointDetailsForResultLog(runId: number, resultId: number, type: TCMContracts.TestLogType, filePath: string): Promise<TCMContracts.TestLogStoreEndpointDetails>;
    getTestLogStoreEndpointDetailsForSubResultLog(runId: number, resultId: number, subResultId: number, type: TCMContracts.TestLogType, filePath: string): Promise<TCMContracts.TestLogStoreEndpointDetails>;
    getTestTagsForBuild(buildId: number): Promise<TCMContracts.TestTag[]>;
    getTestTagsForRelease(releaseId: number, releaseEnvId: number): Promise<TCMContracts.TestTag[]>;
    getTestTagSummaryForBuild(buildId: number): Promise<TCMContracts.TestTagSummary>;
    getTestTagSummaryForRelease(releaseId: number, releaseEnvId: number): Promise<TCMContracts.TestTagSummary>;
    updateTestResultsMetaData(testCaseReferenceId: number, testResultMetaDataUpdateInput: TCMContracts.TestResultMetaDataUpdateInput): Promise<TCMContracts.TestResultMetaData>;
    getTestResultsGroupDetails(pipelineId: number, stageName?: string, phaseName?: string, jobName?: string, shouldIncludeFailedAndAbortedResults?: boolean, queryGroupSummaryForInProgress?: boolean): Promise<TCMContracts.TestResultsDetails>;
    updateTestResults(results: TCMContracts.TestCaseResult[], runId: number): Promise<TCMContracts.TestCaseResult[]>;
    getTestResultsSettings(settingsType?: TCMContracts.TestResultsSettingsType): Promise<TCMContracts.TestResultsSettings>;
    updatePipelinesTestSettings(testResultsUpdateSettings: TCMContracts.TestResultsUpdateSettings): Promise<TCMContracts.TestResultsSettings>;
    getTestResultsMetadata(testCaseReferenceIds: string[], detailsToInclude?: TCMContracts.ResultMetaDataDetails): Promise<TCMContracts.TestResultMetaData[]>;
    getSimilarTestResults(runId: number, testResultId: number, testSubResultId: number): Promise<TCMContracts.TestCaseResult[]>;
    getFolderViewCodeCoverage(buildId: number): Promise<TCMContracts.SourceViewBuildCoverage[]>;
}

export class TestManagementService extends VssService implements ITestManagementService {
    public _serviceStart(pageContext: IVssPageContext) {
        super._serviceStart(pageContext);

        const queryTcmDirectly = "WebAccess.TestManagement.QueryFromTcmService";
        const getBuildCoverageReadOnly = "WebAccess.TestManagement.GetBuildCodeCoverage.EnableSqlReadReplica";
        const getCoverageSummaryReadOnly = "WebAccess.TestManagement.GetCodeCoverageSummary.EnableSqlReadReplica";
        const getQueryTestHistoryReadOnly = "WebAccess.TestManagement.QueryTestHistory.EnableSqlReadReplica";
        const projectContext = getTFSPageData(pageContext);
        this._projectName = projectContext && projectContext.project ? projectContext.project.name : "";

        this._tfsClient = pageContext.getRestClient<Test.ITestRestClient>("ITestRestClient");

        if (isFeatureFlagEnabled(pageContext, getBuildCoverageReadOnly, false)) {
            this._tfsReadOnlyClientForGetBuildCoverage = pageContext.getRestClient<Test.ITestRestClient>("ITestRestClient", { readConsistencyLevel: IVssReadConsistencyLevel.Eventual });
        }
        else {
            this._tfsReadOnlyClientForGetBuildCoverage = pageContext.getRestClient<Test.ITestRestClient>("ITestRestClient");
        }

        if (isFeatureFlagEnabled(pageContext, getCoverageSummaryReadOnly, false)) {
            this._tfsReadOnlyClientForGetCoverageSummary = pageContext.getRestClient<Test.ITestRestClient>("ITestRestClient", { readConsistencyLevel: IVssReadConsistencyLevel.Eventual });
        }
        else {
            this._tfsReadOnlyClientForGetCoverageSummary = pageContext.getRestClient<Test.ITestRestClient>("ITestRestClient");
        }

        if (isFeatureFlagEnabled(pageContext, getQueryTestHistoryReadOnly, false)) {
            this._clientQueryTestHistoryReadOnly = pageContext.getRestClient<Tcm.ITestResultsRestClient>("ITestResultsRestClient", { readConsistencyLevel: IVssReadConsistencyLevel.Eventual });
        }
        else {
            this._clientQueryTestHistoryReadOnly = pageContext.getRestClient<Tcm.ITestResultsRestClient>("ITestResultsRestClient");
        }

        if (isFeatureFlagEnabled(pageContext, queryTcmDirectly, false)) {
            this._extendedClient = pageContext.getRestClient<ExtendedTcm.IExtendedTcmClient>("IExtendedTcmClient");
            this._client = pageContext.getRestClient<Tcm.ITestResultsRestClient>("ITestResultsRestClient");
            this._isTCMClient = true;
        }
        else {
            this._extendedClient = pageContext.getRestClient<Extended.IExtendedTestClient>("IExtendedTestClient");
            this._client = this._tfsClient;
            this._isTCMClient = false;
        }
    }

    public queryTestResultsReportForBuild(buildId: number, publishContext?: string, includeFailureDetails?: boolean): Promise<TCMContracts.TestResultSummary | null> {
        return this._client.queryTestResultsReportForBuild(this._projectName, buildId, publishContext, includeFailureDetails).then((response) => { return response }, (error) => {
            console.log(error);
            return Promise.resolve(null);
        });
    }

    public getTestResultsByGroupForBuild(buildId: number, sourceWorkflow: string, groupBy: string, filter: string, sortBy: string, shouldIncludeResults: boolean, queryRunSummaryForInProgress: boolean): Promise<TCMContracts.TestResultsDetails | null> {
        return this._client.getTestResultDetailsForBuild(this._projectName, buildId, sourceWorkflow, groupBy, filter, sortBy, shouldIncludeResults, queryRunSummaryForInProgress).then((response) => { return response }, (error) => {
            console.log(error);
            return Promise.resolve(null);
        });
    }

    public getTestResultsByGroupForRelease(releaseId: number, releaseEnvId: number, sourceWorkflow: string, groupBy: string, filter: string, sortBy: string, shouldIncludeResults: boolean, queryRunSummaryForInProgress: boolean): Promise<TCMContracts.TestResultsDetails> {
        return this._client.getTestResultDetailsForRelease(this._projectName,
            releaseId,
            releaseEnvId,
            sourceWorkflow,
            groupBy,
            filter,
            sortBy,
            shouldIncludeResults,
            queryRunSummaryForInProgress
        );
    }

    public getTestResultsByQuery(query: TCMContracts.TestResultsQuery): Promise<TCMContracts.TestResultsQuery> {
        return this._client.getTestResultsByQuery(query, this._projectName);
    }

    public getResultById(runId: number, resultsId: number, includeDetails?: TCMContracts.ResultDetails): Promise<TCMContracts.TestCaseResult> {
        return this._client.getTestResultById(this._projectName, runId, resultsId, (includeDetails) ? includeDetails : TCMContracts.ResultDetails.None);
    }

    public getTestRunById(runId: number, includeDetails: boolean, includeTags?: boolean): Promise<TCMContracts.TestRun> {
        if (this._isTCMClient) {
            return (this._client as Tcm.ITestResultsRestClient).getTestRunById(this._projectName, runId, includeDetails, includeTags);
        }

        return this._client.getTestRunById(this._projectName, runId, includeDetails);
    }

    public getCodeCoverageSummary(buildId: number): Promise<TCMContracts.CodeCoverageSummary | null> {
        return this._tfsReadOnlyClientForGetCoverageSummary.getCodeCoverageSummary(this._projectName, buildId);
    }

    public getFolderViewCodeCoverage(buildId: number): Promise<TCMContracts.SourceViewBuildCoverage[]> {
        return (this._client as Tcm.ITestResultsRestClient).fetchSourceCodeCoverageReport(this._projectName, buildId);
    }

    public getBuildCodeCoverage(buildId: number, flag: number): Promise<TCMContracts.BuildCoverage[] | null> {
        return this._tfsReadOnlyClientForGetBuildCoverage.getBuildCodeCoverage(this._projectName, buildId, flag);
    }

    public getTestResultsByBuildWithContinuationToken(buildId: number, publishContext?: string, outcomes?: string, top?: number, continuationToken?: string): Promise<TestCaseResultsWithContinuationToken> {
        return this._extendedClient.getTestResultsByBuildWithContinuationToken(this._projectName, buildId, publishContext, outcomes, top, continuationToken);
    }

    public getTestResultsByPipeline(pipelineId: number, stageName?: string, phaseName?: string, jobName?: string, outcomes?: TCMContracts.TestOutcome[], top?: number, continuationToken?: String): Promise<VS_Services.PagedList<TCMContracts.ShallowTestCaseResult>> {
        return (this._client as Tcm.ITestResultsRestClient).getTestResultsByPipeline(this._projectName, pipelineId, stageName, phaseName, jobName, outcomes, top, continuationToken);
    }

    public getTestResultsByReleaseWithContinuationToken(releaseId: number, releaseEnvId?: number, publishContext?: string, outcomes?: string, top?: number, continuationToken?: string): Promise<TestCaseResultsWithContinuationToken> {
        return this._extendedClient.getTestResultsByReleaseWithContinuationToken(this._projectName, releaseId, releaseEnvId, publishContext, outcomes, top, continuationToken);
    }

    public getResultGroupsByBuild(buildId: number, publishContext?: string, fields?: string[], continuationToken?: string): Promise<TestResultsFieldDetailsWithContinuationToken> {
        return this._extendedClient.getResultGroupsByBuildWithContinuationToken(this._projectName, buildId, publishContext, fields, continuationToken)
    }

    public getResultGroupsByRelease(releaseId: number, releaseEnvId?: number, publishContext?: string, fields?: string[], continuationToken?: string): Promise<TestResultsFieldDetailsWithContinuationToken> {
        return this._extendedClient.getResultGroupsByReleaseWithContinuationToken(this._projectName, releaseId, publishContext, releaseEnvId, fields, continuationToken);
    }

    public queryTestHistory(filter: TCMContracts.TestHistoryQuery): Promise<TCMContracts.TestHistoryQuery> {
        return this._clientQueryTestHistoryReadOnly.queryTestHistory(filter, this._projectName);
    }

    public getRecentBugs(automatedTestName?: string, testCaseId?: number, recentDays?: number): Promise<TCMContracts.WorkItemReference[]> {
        const WorkItemCategoryBug = "Microsoft.BugCategory";
        return this._client.queryTestResultWorkItems(
            this._projectName,
            WorkItemCategoryBug,
            automatedTestName,
            testCaseId,
            undefined,
            recentDays);
    }

    public getAssociatedBugs(runId: number, resultId: number): Promise<TCMContracts.WorkItemReference[]> {
        return this._client.getBugsLinkedToTestResult(this._projectName, runId, resultId);
    }

    public deleteTestMethodToWorkItemLink(testName: string, workItemId: number): Promise<boolean> {
        return this._client.deleteTestMethodToWorkItemLink(this._projectName, testName, workItemId);
    }

    public getTestMethodLinkedWorkItems(testName: string): Promise<TCMContracts.TestToWorkItemLinks> {
        return this._client.queryTestMethodLinkedWorkItems(this._projectName, testName);
    }

    public getTestRunAttachments(runId: number): Promise<TCMContracts.TestAttachment[]> {
        return this._client.getTestRunAttachments(this._projectName, runId);
    }

    public getTestResultAttachments(runId: number, testCaseResultId: number): Promise<TCMContracts.TestAttachment[]> {
        return this._client.getTestResultAttachments(this._projectName, runId, testCaseResultId);
    }

    public getTestSubResultAttachments(runId: number, testCaseResultId: number, testSubResultId: number): Promise<TCMContracts.TestAttachment[]> {
        return this._client.getTestSubResultAttachments(this._projectName, runId, testCaseResultId, testSubResultId);
    }

    public getTestRunAttachmentContent(runId: number, attachmentId: number): Promise<ArrayBuffer> {
        return this._client.getTestRunAttachmentContent(this._projectName, runId, attachmentId);
    }

    public getTestResultAttachmentContent(runId: number, testCaseResultId: number, attachmentId: number): Promise<ArrayBuffer> {
        return this._client.getTestResultAttachmentContent(this._projectName, runId, testCaseResultId, attachmentId);
    }

    public getTestSubResultAttachmentContent(runId: number, testCaseResultId: number, attachmentId: number, testSubResultId: number): Promise<ArrayBuffer> {
        return this._client.getTestSubResultAttachmentContent(this._projectName, runId, testCaseResultId, attachmentId, testSubResultId);
    }

    public getTestRunLogs(runId: number, type: TCMContracts.TestLogType, directoryPath?: string, fileNamePrefix?: string, fetchMetaData?: boolean, top?: number, continuationToken?: string): Promise<ITestLogWithContinuationToken> {
        return (this._extendedClient as ExtendedTcm.IExtendedTcmClient).getTestRunLogs(this._projectName, runId, type, directoryPath, fileNamePrefix, fetchMetaData, top, continuationToken);
    }

    public getTestResultLogs(runId: number, resultId: number, type: TCMContracts.TestLogType, directoryPath?: string, fileNamePrefix?: string, fetchMetaData?: boolean, top?: number, continuationToken?: string): Promise<ITestLogWithContinuationToken> {
        return (this._extendedClient as ExtendedTcm.IExtendedTcmClient).getTestResultLogs(this._projectName, runId, resultId, type, directoryPath, fileNamePrefix, fetchMetaData, top, continuationToken);
    }

    public getTestSubResultLogs(runId: number, resultId: number, subResultId: number, type: TCMContracts.TestLogType, directoryPath?: string, fileNamePrefix?: string, fetchMetaData?: boolean, top?: number, continuationToken?: string): Promise<ITestLogWithContinuationToken> {
        return (this._extendedClient as ExtendedTcm.IExtendedTcmClient).getTestSubResultLogs(this._projectName, runId, resultId, subResultId, type, directoryPath, fileNamePrefix, fetchMetaData, top, continuationToken);
    }

    public getTestLogStoreEndpointDetailsForRunLog(runId: number, type: TCMContracts.TestLogType, filePath: string): Promise<TCMContracts.TestLogStoreEndpointDetails> {
        return (this._client as Tcm.ITestResultsRestClient).getTestLogStoreEndpointDetailsForRunLog(this._projectName, runId, type, filePath);
    }

    public getTestLogStoreEndpointDetailsForResultLog(runId: number, resultId: number, type: TCMContracts.TestLogType, filePath: string): Promise<TCMContracts.TestLogStoreEndpointDetails> {
        return (this._client as Tcm.ITestResultsRestClient).getTestLogStoreEndpointDetailsForResultLog(this._projectName, runId, resultId, type, filePath);
    }

    public getTestLogStoreEndpointDetailsForSubResultLog(runId: number, resultId: number, subResultId: number, type: TCMContracts.TestLogType, filePath: string): Promise<TCMContracts.TestLogStoreEndpointDetails> {
        return (this._client as Tcm.ITestResultsRestClient).getTestLogStoreEndpointDetailsForSubResultLog(this._projectName, runId, resultId, subResultId, type, filePath);
    }

    public getTestTagsForBuild(buildId: number): Promise<TCMContracts.TestTag[]> {
        return (this._client as Tcm.ITestResultsRestClient).getTestTagsForBuild(this._projectName, buildId);
    }

    public getTestTagsForRelease(releaseId: number, releaseEnvId: number): Promise<TCMContracts.TestTag[]> {
        return (this._client as Tcm.ITestResultsRestClient).getTestTagsForRelease(this._projectName, releaseId, releaseEnvId);
    }

    public getTestTagSummaryForBuild(buildId: number): Promise<TCMContracts.TestTagSummary> {
        return (this._client as Tcm.ITestResultsRestClient).getTestTagSummaryForBuild(this._projectName, buildId);
    }

    public getTestTagSummaryForRelease(releaseId: number, releaseEnvId: number): Promise<TCMContracts.TestTagSummary> {
        return (this._client as Tcm.ITestResultsRestClient).getTestTagSummaryForRelease(this._projectName, releaseId, releaseEnvId);
    }

    public updateTestResultsMetaData(testCaseReferenceId: number, testResultMetaDataUpdateInput: TCMContracts.TestResultMetaDataUpdateInput): Promise<TCMContracts.TestResultMetaData> {
        return (this._client as Tcm.ITestResultsRestClient).updateTestResultsMetaData(testResultMetaDataUpdateInput, this._projectName, testCaseReferenceId);
    }

    public getTestResultsGroupDetails(pipelineId: number, stageName?: string, phaseName?: string, jobName?: string,
        shouldIncludeFailedAndAbortedResults?: boolean, queryGroupSummaryForInProgress?: boolean): Promise<TCMContracts.TestResultsDetails> {
        return (this._client as Tcm.ITestResultsRestClient).testResultsGroupDetails(this._projectName, pipelineId, stageName, phaseName,
            jobName, shouldIncludeFailedAndAbortedResults, queryGroupSummaryForInProgress);
    }

    public updateTestResults(results: TCMContracts.TestCaseResult[], runId: number): Promise<TCMContracts.TestCaseResult[]> {
        return (this._client as Tcm.ITestResultsRestClient).updateTestResults(results, this._projectName, runId);
    }

    public getTestResultsSettings(settingsType?: TCMContracts.TestResultsSettingsType): Promise<TCMContracts.TestResultsSettings> {
        return (this._client as Tcm.ITestResultsRestClient).getTestResultsSettings(this._projectName, settingsType);
    }

    public updatePipelinesTestSettings(testResultsUpdateSettings: TCMContracts.TestResultsUpdateSettings): Promise<TCMContracts.TestResultsSettings> {
        return (this._client as Tcm.ITestResultsRestClient).updatePipelinesTestSettings(testResultsUpdateSettings, this._projectName);
    }

    public getTestResultsMetadata(testCaseReferenceIds: string[], detailsToInclude?: TCMContracts.ResultMetaDataDetails): Promise<TCMContracts.TestResultMetaData[]> {
        return (this._client as Tcm.ITestResultsRestClient).queryTestResultsMetaData(testCaseReferenceIds, this._projectName, detailsToInclude);
    }

    public async getSimilarTestResults(runId: number, testResultId: number, testSubResultId: number): Promise<TCMContracts.TestCaseResult[]> {
        return (this._client as Tcm.ITestResultsRestClient).getSimilarTestResults(this._projectName,runId, testResultId, testSubResultId);
    }

    private _projectName: string;
    private _client: Tcm.ITestResultsRestClient | Test.ITestRestClient;
    private _clientQueryTestHistoryReadOnly: Tcm.ITestResultsRestClient | Test.ITestRestClient;
    private _tfsClient: Test.ITestRestClient;
    private _tfsReadOnlyClientForGetBuildCoverage: Test.ITestRestClient;
    private _tfsReadOnlyClientForGetCoverageSummary: Test.ITestRestClient;
    private _isTCMClient: boolean;

    private _extendedClient: Extended.IExtendedTestClient | ExtendedTcm.IExtendedTcmClient;

}

Services.add(TestManagementServiceName, { serviceFactory: TestManagementService });
